import type { StaffMemoryCommandOutcome, StaffMemoryFact } from "@lafayette/schemas";
import { useState } from "react";

import type { MemoryCommand, MemoryCommandResult } from "../commands";
import { Compare } from "./Compare";
import { MemoryDialog, type Reason, ReasonSelect, useCommandForm } from "./MemoryDialog";

type Props = Readonly<{
  candidate: StaffMemoryFact;
  challenged: StaffMemoryFact;
  now: Date;
  run: (request: MemoryCommand) => Promise<MemoryCommandResult>;
  onDone: (outcome: StaffMemoryCommandOutcome) => void;
  onClose: () => void;
}>;

/**
 * M-S7. The candidate replaces the memory it challenges: a new memory holds the candidate's value, the
 * old one moves to history and the candidate itself is removed. The command names BOTH versions the page
 * read, so a change to either one stops it.
 */
export function AcceptDialog({ candidate, challenged, now, run, onDone, onClose }: Props) {
  const [reason, setReason] = useState<Reason>("");
  const [reasonError, setReasonError] = useState<string | undefined>();
  const form = useCommandForm(onDone);

  function validate() {
    setReasonError(reason === "" ? "Choose a reason." : undefined);
    return reason !== "";
  }

  function submit() {
    if (reason === "") return;
    void form.submit(() =>
      run({
        command: "accept",
        factId: candidate.id,
        body: {
          expectedVersion: candidate.version,
          expectedConflictVersion: challenged.version,
          reasonCode: reason,
        },
      }),
    );
  }

  const reasonInvalid = form.failure?.invalidField === "reasonCode";
  return (
    <MemoryDialog
      title="Accept this candidate?"
      description="The candidate replaces the current memory. The current memory moves to history."
      confirmLabel="Replace current memory"
      failure={reasonInvalid ? null : form.failure}
      pending={form.pending}
      validate={validate}
      onSubmit={submit}
      onClose={onClose}
      dialogClassName="[--dialog-width:48.75rem]"
    >
      <Compare current={challenged} candidate={candidate} now={now} />
      <ReasonSelect
        value={reason}
        onChange={setReason}
        error={reasonInvalid ? form.failure?.message : reasonError}
      />
    </MemoryDialog>
  );
}
